import React, { useCallback, useState } from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import { cssMap } from '@atlaskit/css';
import PersonLockLockedIcon from '@atlaskit/icon-lab/core/person-lock-locked';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import type { JsmSolutionComposerPlanSummarySection } from '@atlassian/conversation-assistant-message-actions-api/jsm';
import { ChatGenericError } from '@atlassian/conversation-assistant-ui-components/ui/ChatGenericError';

import { TaskCardContent } from './card-content';
import { TaskCardFooter } from './card-footer';
import { TaskCardHeader } from './card-header';
import type { CardMetadataSummaryItem } from './card-metadata-summary/CardMetadataSummary';
import { messages } from './messages';

const styles = cssMap({
	container: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		borderRadius: token('radius.large'),
		backgroundColor: token('elevation.surface.raised'),
		boxShadow: token('elevation.shadow.raised'),
	},
});

const ChannelAccessValue = (): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Inline as="span" space="space.050" alignBlock="center">
			<PersonLockLockedIcon
				label=""
				color="currentColor"
				testId="jsm-solution-composer-plan-task-card.channel-access-icon"
			/>
			{formatMessage(messages.channelAccessClosedValue)}
		</Inline>
	);
};

export type TaskCardProps = {
	title: string;
	sections: JsmSolutionComposerPlanSummarySection[];
	onCreateSpace: () => void;
	isExecuted?: boolean;
	projectKey?: string;
	projectName?: string;
	isDisabled?: boolean;
};

export const TaskCard = ({
	title,
	sections,
	onCreateSpace,
	isExecuted,
	projectKey,
	projectName,
	isDisabled,
}: TaskCardProps): React.JSX.Element => {
	di(TaskCardHeader, TaskCardContent, TaskCardFooter);
	const { formatMessage } = useIntl();
	const [isExpanded, setIsExpanded] = useState(true);
	const [hasBeenActioned, setHasBeenActioned] = useState(false);

	const handleActioned = useCallback(() => setHasBeenActioned(true), []);

	const isSpaceKeyValidationEnabled = fg('rovo_chat_jsm_composer_s2_space_key_validation');

	const summaryItems: CardMetadataSummaryItem[] | undefined = isSpaceKeyValidationEnabled
		? [
				{ label: formatMessage(messages.spaceNameLabel), value: projectName ?? title },
				{ label: formatMessage(messages.spaceKeyLabel), value: projectKey ?? '' },
				{ label: formatMessage(messages.channelAccessLabel), value: <ChannelAccessValue /> },
			]
		: undefined;

	return (
		<ErrorBoundary fallback={<ChatGenericError errorType="generic" />}>
			<Box xcss={styles.container} testId="jsm-solution-composer-plan-task-card">
				<TaskCardHeader
					title={isSpaceKeyValidationEnabled ? formatMessage(messages.cardTitle) : title}
					isExpanded={isExpanded}
					onToggle={() => setIsExpanded(!isExpanded)}
				/>
				{isExpanded ? (
					<>
						<TaskCardContent sections={sections} summaryItems={summaryItems} />
						<TaskCardFooter
							onCreateSpace={onCreateSpace}
							isExecuted={isExecuted}
							projectKey={projectKey}
							isDisabled={isDisabled}
							hasBeenActioned={hasBeenActioned}
							onActioned={handleActioned}
						/>
					</>
				) : null}
			</Box>
		</ErrorBoundary>
	);
};
